<template>
  <li class="education-description-tab">
    <h4>{{ $t("education.about") }}</h4>
    <p class="education-description-tab__description">{{ item.description }}</p>

    <div v-if="hashtags.length" class="education-description-tab__hashtags">
      <span v-for="(hashtag, index) in hashtags"
            :key="hashtag.id || 'hashtag_' + index"
            class="btn btn-small btn-light"
      >
        #{{ hashtag.name }}
      </span>
    </div>

    <div v-if="successMessage"
         class="uk-alert-success bg-gray-700 education-description-tab__alert"
         uk-alert
    >
      <a class="uk-alert-close" uk-close></a>
      <p>
        <i aria-hidden="true" class="fa fa-check"></i>
        {{ successMessage }}
      </p>
    </div>

    <ul class="course-curriculum" uk-accordion="multiple: true">
      <li v-for="(section, sectionIndex) in sections"
          :key="section.id || 'training_section_' + sectionIndex"
          :class="{ 'uk-open': sectionIndex === 0 }"
      >
        <a class="uk-accordion-title" href="#">{{ section.name }}</a>
        <div class="uk-accordion-content">
          <ul class="course-curriculum-list">
            <ContentItem v-for="(content, contentIndex) in getSectionContents(section)"
                         :key="content.id || 'content_' + contentIndex"
                         :content-item="content"
                         :icon="content.file_category.icon"
                         :is_completed="false"
                         :is_demo="content.is_demo"
                         :is_locked="false"
                         :is_unlocked="true"
                         :name="$t(content.file_category.key_name)"
                         :point="content.score"
                         :title="content.name"
                         @linkClick="$emit('content-select', content)"
            />
          </ul>
        </div>
      </li>
    </ul>

    <div id="admin_content_modal"
         bg-close="true"
         class="p-0 content-modal-wrapper education-content-modal"
         esc-close="true"
         uk-modal
    >
      <div v-if="selectedContent"
           class="uk-modal-dialog bg-transparent content-modal-dialog"
      >
        <div v-if="!isScormContent" class="content-modal-header">
          <button class="content-close-btn uk-modal-close-default" type="button" uk-close></button>
          <h2 class="content-modal-title">{{ contentTitle }}</h2>
        </div>
        <button v-else
                class="content-close-btn uk-modal-close-default"
                type="button"
                uk-close
        ></button>

        <div class="uk-modal-body py-0 content-modal-body no-footer">
          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.visualDocument"
               class="video-responsive"
          >
            <img :alt="contentTitle" :src="fileLink"/>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.audioDocument"
               class="video-responsive"
          >
            <audio :src="fileLink" controls></audio>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.videoDocument"
               class="video-responsive"
          >
            <VideoPlayer v-if="fileLink" :video-url="fileLink"/>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.videoUrl"
               class="ad-content-frame"
          >
            <iframe :src="getEmbedUrl(selectedContent.training_content || selectedContent.url || selectedContent.link)"
                    :title="contentTitle"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                    allowfullscreen
                    frameborder="0"
            ></iframe>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.pdfDocument"
               class="ad-content-frame"
          >
            <PdfViewer :src="fileLink" mode="scroll"/>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.url"
               class="ad-content-frame url-content-container"
          >
            <div class="url-content-info">
              <i class="uil-link-alt url-icon"></i>
              <p class="url-description">{{ $t("general.url_external_info") }}</p>
              <a :href="externalUrl"
                 class="btn btn-primary btn-lg"
                 rel="noopener noreferrer"
                 target="_blank"
              >
                <i class="uil-external-link-alt mr-2"></i>
                {{ $t("general.go_to_link") }}
              </a>
            </div>
          </div>

          <div v-if="+selectedContent.training_file_type_id === ANZERA_TYPE_ID"
               class="ad-content-frame"
          >
            <iframe :src="externalPackageUrl"
                    :title="contentTitle"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                    allowfullscreen
                    frameborder="0"
            ></iframe>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.scormPackage"
               class="ad-content-frame"
          >
            <iframe :src="scormPackageUrl"
                    :title="contentTitle"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                    allowfullscreen
                    frameborder="0"
            ></iframe>
          </div>

          <div v-if="+selectedContent.training_file_type_id === CATEGORY_IDS.informationContent"
               class="content-modal-html"
          >
            <div v-html="selectedContent.information_content || selectedContent.training_content"></div>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import ContentItem from "@/view/components/assignmentDetail/ContentItem";
import PdfViewer from "@/view/components/ui/PdfViewer.vue";
import VideoPlayer from "@/view/components/video/VideoPlayer.vue";
import { CATEGORY_IDS } from "@/core/constant/content-types";
import { toYouTubeEmbedUrl, isYouTubeUrl } from "@/utils/videojsUtils";

const ANZERA_TYPE_ID = 10;

export default {
  name: "EducationDescriptionTab",
  components: {
    ContentItem,
    PdfViewer,
    VideoPlayer,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    selectedContent: {
      type: Object,
      default: null,
    },
    scormPackageUrl: {
      type: String,
      default: null,
    },
    externalPackageUrl: {
      type: String,
      default: null,
    },
    successMessage: {
      type: String,
      default: null,
    },
  },
  computed: {
    CATEGORY_IDS() {
      return CATEGORY_IDS;
    },
    ANZERA_TYPE_ID() {
      return ANZERA_TYPE_ID;
    },
    hashtags() {
      return Array.isArray(this.item.hashtag) ? this.item.hashtag : [];
    },
    sections() {
      return Array.isArray(this.item.training_section)
          ? this.item.training_section
          : [];
    },
    contentTitle() {
      if (!this.selectedContent) return "";
      return this.selectedContent.name || this.selectedContent.title || "";
    },
    fileLink() {
      if (!this.selectedContent || !this.selectedContent.file) return "";
      return this.selectedContent.file.file_link || this.selectedContent.file;
    },
    externalUrl() {
      if (!this.selectedContent) return "";
      return this.selectedContent.link
          || this.selectedContent.url
          || this.selectedContent.training_content
          || "";
    },
    isScormContent() {
      if (!this.selectedContent) return false;
      const id = +this.selectedContent.training_file_type_id;
      return id === CATEGORY_IDS.scormPackage || id === ANZERA_TYPE_ID;
    },
  },
  methods: {
    getSectionContents(section) {
      return Array.isArray(section.training_content)
          ? section.training_content.filter(
              (content) => content && content.file_category
            )
          : [];
    },
    getEmbedUrl(url) {
      if (isYouTubeUrl(url)) {
        return toYouTubeEmbedUrl(url);
      }
      return url;
    },
  },
};
</script>

<style scoped>
.education-description-tab__description {
  margin-bottom: 16px;
  color: #475569;
  font-size: 16px;
  line-height: 1.6;
}

.education-description-tab__hashtags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.education-description-tab__alert {
  min-height: 36px;
  margin-bottom: 16px;
}

.education-description-tab__alert p {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #15803d;
}

.course-curriculum {
  margin-bottom: 0;
}

.uk-accordion-title {
  min-height: 48px;
  box-sizing: border-box;
}

/* Kategori rozeti satırın sol üst köşesinde, yarısı dışarı taşacak şekilde */
::v-deep .course-curriculum-list > li {
  overflow: visible;
  padding-top: 26px;
}

::v-deep .course-curriculum-list > li .icon-cat {
  position: absolute;
  top: 0;
  left: 14px;
  z-index: 1;
  margin-right: 0;
  transform: translateY(-50%);
}

/* li overflow'u açıldığı için uzun başlığı link seviyesinde kes */
::v-deep .course-curriculum-list > li .assignment-content-link {
  overflow: hidden;
  min-width: 0;
}

@media (max-width: 767px) {
  ::v-deep .course-curriculum-list > li {
    padding-top: 24px;
  }

  ::v-deep .course-curriculum-list > li .icon-cat {
    left: 10px;
  }
}

.education-content-modal {
  z-index: 9999999;
  background: #dddddd !important;
  padding: 0 !important;
}

.content-modal-dialog {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  height: 100vh !important;
  height: 100dvh !important;
  max-height: 100vh !important;
  max-height: 100dvh !important;
}

.content-modal-header {
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  padding: 0 16px !important;
  height: 48px !important;
  background: #f8f8f8 !important;
  border-bottom: 1px solid #e0e0e0 !important;
  position: relative !important;
  box-sizing: border-box !important;
}

.content-modal-header .content-close-btn {
  position: static !important;
  flex-shrink: 0 !important;
  width: 32px !important;
  height: 32px !important;
  background: transparent !important;
  color: #333 !important;
  order: 3 !important;
}

.content-modal-header .content-close-btn:hover {
  background: rgba(0, 0, 0, 0.08) !important;
}

.content-modal-title {
  flex: 1 !important;
  margin: 0 !important;
  padding: 0 12px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  line-height: 1.3 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  text-align: center !important;
  order: 1 !important;
  min-width: 0 !important;
}

.content-modal-body {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  overflow: auto !important;
  -webkit-overflow-scrolling: touch;
  padding: 0 !important;
  margin: 0 !important;
  max-height: none !important;
  display: flex !important;
  flex-direction: column !important;
  width: 100%;
}

.content-modal-body.no-footer {
  max-height: none !important;
}

.content-close-btn {
  position: absolute !important;
  top: 10px !important;
  right: 10px !important;
  z-index: 2147483647 !important;
  width: 36px !important;
  height: 36px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  border-radius: 50% !important;
  color: #333 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
}

.ad-content-frame {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.ad-content-frame iframe {
  flex: 1 1 auto;
  min-height: 0;
  height: 100% !important;
  width: 100% !important;
  max-width: 100%;
  border: none;
}

.content-modal-body .video-responsive {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-modal-body .video-responsive img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ad-content-frame .pdf-viewer {
  flex: 1;
  min-height: 0;
}

.ad-content-frame ::v-deep .pdf-viewer__container {
  max-height: none !important;
  flex: 1;
}

.content-modal-html {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
}

.content-modal-html img,
.content-modal-html video,
.content-modal-html iframe {
  max-width: 100%;
  height: auto;
}

.url-content-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 0;
  padding: 24px 16px;
  background-color: #111;
  height: 100%;
  box-sizing: border-box;
}

.url-content-info {
  text-align: center;
  color: #fff;
  max-width: 420px;
  width: 100%;
}

.url-icon {
  font-size: 64px;
  color: #ae71ff;
  margin-bottom: 20px;
  display: block;
}

.url-description {
  font-size: 18px;
  margin-bottom: 30px;
  color: #ccc;
}

@media (max-width: 768px) {
  .content-modal-header .content-close-btn {
    order: 0 !important;
  }

  .content-modal-title {
    order: 1 !important;
  }

  .content-close-btn {
    right: auto !important;
    left: 10px !important;
  }

  .content-modal-html {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .content-modal-header {
    height: 44px !important;
    padding: 0 12px !important;
  }

  .content-modal-title {
    font-size: 14px !important;
  }

  .url-icon {
    font-size: 48px;
  }

  .url-description {
    font-size: 15px;
    margin-bottom: 20px;
  }
}
</style>
